import { ButtonPrimary, ButtonTertiary, IconButton } from "@nova/component-btn"
import { TriCheckbox } from "@nova/component-checkbox"
import * as Icons from "@nova/component-icons"
import { Status } from "@nova/ds-component-status"
import { Table } from "@nova/ds-component-table"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { formatAltitude, formatDistance, formatSpeed } from "@nova/format"
import { MissionTableItem, MissionOriginRouterState } from "@nova/schemas"
import { MemberType } from "@nova/constants"
import classNames from "classnames"
import { FunctionComponent, useMemo } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectOpenProjectDropdownFlightId,
    selectTagDropdownFlightId,
} from "../redux/missions-table-selectors"
import {
    selectMissionRange,
    toggleMissionSelected,
    type MissionsTableColumn,
} from "../redux/missions-table-slice"
import { setGlobalNotificationContext } from "user/profile/slice"
import {
    getGenericDangerConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import { OpenProjectButton } from "../OpenProjectButton"
import { MissionTagDropdown } from "./MissionTagDropdown"
import { useDeleteMission } from "./PilotTrackingMissionQueries"
import { useGroupDrop, useMissionDrag, useUngroupDrop } from "./useMissionTableDnD"
import { useRouter } from "pages"
import { LinkText } from "components/LinkText"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { selectWorkspaceDistanceUnit } from "features/data-management/workspace/WorkspaceManagementSlice"
import { getDateTimeString } from "@nova/util-strings"
import { selectAllRegisteredMembers } from "features/entity-caching/members/UserManagementSlice"

const formatDuration = (seconds: number) => {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = (seconds % 60).toFixed(0)
    return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
}

let anchorId: string | null = null

const idsBetween = (from: string, to: string) => {
    const ids = [...document.querySelectorAll<HTMLElement>("[data-mission-id]")].map(
        (r) => r.dataset.missionId!
    )
    const [a, b] = [ids.indexOf(from), ids.indexOf(to)].sort((x, y) => x - y)
    return a === -1 ? [] : ids.slice(a, b + 1)
}

interface MissionRowProps {
    mission: MissionTableItem<"mission">
    selectedIds: Record<string, boolean>
    onMutate: () => void
    columns: MissionsTableColumn[]
    selectable?: boolean
    editable?: boolean
    nested?: boolean
    groupId?: string
    origin?: MissionOriginRouterState
    onPreviewChange?: (data: { anchorRect: DOMRect; missionId: string } | null) => void
    onHoverChange?: (missionId: string | null) => void
}

export const MissionRow: FunctionComponent<MissionRowProps> = ({
    mission,
    selectedIds,
    onMutate,
    columns,
    selectable = true,
    editable = true,
    nested = false,
    groupId,
    origin,
    onPreviewChange,
    onHoverChange,
}) => {
    const col = (c: MissionsTableColumn) => columns.includes(c)
    const dispatch = useAppDispatch()
    const router = useRouter("/workspace/:workspaceId")
    const users = useAppSelector(selectAllRegisteredMembers)
    const pilotName = useMemo(
        () =>
            users.find((u) => u.type === MemberType.Member && u.id === mission.user_id)?.fullName ??
            null,
        [users, mission.user_id]
    )
    const { isEditor } = useWorkspacePermissions()
    const distanceUnit = useAppSelector(selectWorkspaceDistanceUnit)
    const confirmationModal = useConfirmationModalContext()
    const { deleteMission } = useDeleteMission(mission.id)
    const tagDropdownFlightId = useAppSelector(selectTagDropdownFlightId)
    const openProjectDropdownFlightId = useAppSelector(selectOpenProjectDropdownFlightId)
    const dropdownOpen =
        tagDropdownFlightId === mission.id || openProjectDropdownFlightId === mission.id

    const { isDragging, dragRef } = useMissionDrag(
        mission.id,
        groupId ?? null,
        new Date(mission.started_at).toDateString(),
        mission.aircraft?.name ?? null
    )

    const { isOver: groupIsOver, dropRef: groupDropRef } = useGroupDrop(groupId ?? "", !!groupId)
    const { isOver: ungroupIsOver, dropRef: ungroupDropRef } = useUngroupDrop(!groupId)

    const isOver = groupId ? groupIsOver : ungroupIsOver
    const dropRef = groupId ? groupDropRef : ungroupDropRef

    const title = useMemo(() => {
        if (!mission.started_at) return "Unknown date"
        return getDateTimeString(new Date(mission.started_at))
    }, [mission.started_at])

    return (
        <>
            <Table.Row
                ref={(node) => {
                    dragRef(node)
                    dropRef(node)
                }}
                data-drag-type="touch"
                data-mission-id={mission.id}
                onMouseEnter={() => onHoverChange?.(mission.id)}
                onMouseLeave={() => onHoverChange?.(null)}
                className={classNames("relative transition-opacity group/mission-row", {
                    "opacity-40": isDragging,
                })}
            >
                {selectable && (
                    <Table.Cell className="w-10 h-[68px] pl-4">
                        {isOver && (
                            <div
                                className={classNames(
                                    "absolute bottom-0 right-0 h-0.5 bg-white/80 pointer-events-none",
                                    { "left-0": !groupId, "left-10": !!groupId }
                                )}
                            />
                        )}
                        <TriCheckbox
                            size="small"
                            state={!!selectedIds[mission.id] ? "checked" : "unchecked"}
                            onClick={(e) => {
                                e.stopPropagation()
                                const range =
                                    e.shiftKey && anchorId ? idsBetween(anchorId, mission.id) : []
                                anchorId = mission.id
                                dispatch(
                                    range.length
                                        ? selectMissionRange(range)
                                        : toggleMissionSelected(mission.id)
                                )
                            }}
                        />
                    </Table.Cell>
                )}
                <Table.Cell className="w-8 !pl-2">
                    {/* Empty cell to accomodate group chevron */}
                </Table.Cell>
                {col("started_at") && (
                    <Table.Cell
                        className="!pl-1.5"
                        onMouseEnter={(e) => {
                            onPreviewChange?.({
                                anchorRect: e.currentTarget.getBoundingClientRect(),
                                missionId: mission.id,
                            })
                        }}
                        onMouseLeave={() => onPreviewChange?.(null)}
                    >
                        <LinkText>
                            <router.Link
                                to={{
                                    path: "/fleet/flights/:missionId",
                                    params: { missionId: mission.id },
                                }}
                                className={classNames("flex items-center gap-1", {
                                    "pl-7": nested,
                                })}
                                state={{ origin }}
                            >
                                <Icons.SmallIcon Icon={Icons.Distance} />
                                {title}
                            </router.Link>
                        </LinkText>
                    </Table.Cell>
                )}
                {col("pilot") && (
                    <Table.Cell className="min-w-[80px]">
                        {mission.user_id && pilotName ? (
                            <router.Link
                                to={{
                                    path: "/fleet/pilots/:pilotId",
                                    params: { pilotId: mission.user_id },
                                }}
                            >
                                <LinkText>{pilotName}</LinkText>
                            </router.Link>
                        ) : (
                            "--"
                        )}
                    </Table.Cell>
                )}
                {col("drone") && (
                    <Table.Cell>
                        {mission.aircraft ? (
                            <router.Link
                                to={{
                                    path: "/fleet/aircraft/:aircraftId",
                                    params: { aircraftId: mission.aircraft.id },
                                }}
                            >
                                <LinkText>{mission.aircraft.name}</LinkText>
                            </router.Link>
                        ) : (
                            "--"
                        )}
                    </Table.Cell>
                )}
                {col("flightTime") && <Table.Cell>{formatDuration(mission.flightTime)}</Table.Cell>}
                {col("distanceFlown") && (
                    <Table.Cell>
                        {mission.distanceFlown == null
                            ? "--"
                            : formatDistance({
                                  value: mission.distanceFlown,
                                  unit: distanceUnit,
                              })}
                    </Table.Cell>
                )}
                {col("maxAltitude") && (
                    <Table.Cell>
                        {mission.maxAltitude == null
                            ? "--"
                            : formatAltitude({
                                  value: mission.maxAltitude,
                                  unit: distanceUnit,
                              })}
                    </Table.Cell>
                )}
                {col("maxSpeed") && (
                    <Table.Cell>
                        {mission.maxSpeed == null
                            ? "--"
                            : formatSpeed({ value: mission.maxSpeed, unit: distanceUnit })}
                    </Table.Cell>
                )}
                {col("tags") && (
                    <Table.Cell className="whitespace-normal">
                        <MissionTagDropdown
                            flightId={mission.id}
                            missions={[
                                { missionId: mission.id, tagIds: mission.tags.map((t) => t.id) },
                            ]}
                            currentTags={mission.tags}
                            onMutate={onMutate}
                        />
                    </Table.Cell>
                )}
                {col("numChecklistSubmissions") && (
                    <Table.Cell className="pr-2">
                        <Status
                            type={mission.numChecklistSubmissions === 0 ? "error" : "positive"}
                            variant="outline"
                        >
                            {mission.numChecklistSubmissions === 0
                                ? "Missing"
                                : `${mission.numChecklistSubmissions} Completed`}
                        </Status>
                    </Table.Cell>
                )}
                <Table.Cell className="sticky right-0 w-px p-0">
                    <div
                        className={classNames(
                            "absolute right-0 top-px bottom-px items-center gap-4 bg-gray-800 px-4 py-2 z-10 flex transition-opacity pointer-events-none opacity-0 group-hover/mission-row:pointer-events-auto group-hover/mission-row:opacity-100",
                            { "!pointer-events-auto !opacity-100": dropdownOpen }
                        )}
                    >
                        <OpenProjectButton
                            flightId={mission.id}
                            projectIds={mission.linkedProjectIds}
                            SingleButtonComponent={ButtonTertiary}
                            MultipleButtonComponent={ButtonTertiary}
                        />
                        <ButtonPrimary
                            title="View Flight"
                            RightIcon={Icons.ArrowRight}
                            onClick={(e) => {
                                e.stopPropagation()
                                router.navigate(
                                    {
                                        path: "/fleet/flights/:missionId",
                                        params: { missionId: mission.id },
                                    },
                                    { state: { origin } }
                                )
                            }}
                        />
                        {isEditor && editable && (
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation()
                                    confirmationModal.confirm({
                                        RenderComponent: getGenericDangerConfirmationModalComponent(
                                            {
                                                title: "Delete Flight?",
                                                message:
                                                    "Deleting the flight removes all data and metrics from tracking, do you want to proceed?",
                                                dangerMessage: "This cannot be undone.",
                                            }
                                        ),
                                        asyncConfirmCallback: async () => {
                                            try {
                                                await deleteMission()
                                                dispatch(
                                                    setGlobalNotificationContext({
                                                        message: "1 flight has been deleted.",
                                                        status: GlobalNotificationStatus.success,
                                                        timeoutInMs: 3000,
                                                    })
                                                )
                                            } catch {
                                                dispatch(
                                                    setGlobalNotificationContext({
                                                        message:
                                                            "Failed to delete 1 flight. Please try again.",
                                                        status: GlobalNotificationStatus.error,
                                                        timeoutInMs: 3000,
                                                    })
                                                )
                                            }
                                        },
                                    })
                                }}
                            >
                                <Icons.SmallIcon Icon={Icons.Trash} />
                            </IconButton>
                        )}
                    </div>
                </Table.Cell>
            </Table.Row>
        </>
    )
}
